/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Fragment, useEffect, useRef, useState } from 'react';

import { permissionsQuery } from '@/api/auth';
import { type Hit, KIND_LABEL, searchQuery } from '@/api/search';
import { Icon } from '@/components/ui/Icon';
import type { IconName } from '@/components/ui/icons';
import { Modal } from '@/components/ui/Modal';
import { Rows } from '@/components/ui/States';
import { type HelpMatch, helpMatches } from '@/lib/help';
import { type Place, placesMatching } from '@/lib/searchIndex';

/** How long to wait after the last keystroke before asking. */
const SETTLE_MS = 180;

/** How many help topics sit under the results. */
const HELP_SHOWN = 5;

/** How many places in Vellum -- screens, cards, settings -- sit under them. */
const PLACES_SHOWN = 5;

/** The longest line of a topic's lede shown under its title. */
const SUMMARY_MOST = 110;

/**
 * The guide, fetched the first time somebody searches and then kept: it is the
 * same for everybody and changes only with a release, so there is nothing to
 * ask again. Imported rather than bundled, so opening search never downloads
 * the whole guide for somebody who is looking for an agreement.
 */
const guideQuery = {
  queryKey: ['help-guide'] as const,
  queryFn: () => import('@/lib/help/content').then((module) => module.HELP),
  staleTime: Infinity,
  gcTime: Infinity,
};

/**
 * One row of the list: something of ours from the server, a place in Vellum,
 * a help topic, or the whole Agreements list searched for the term.
 */
type Choice =
  | { kind: 'hit'; hit: Hit }
  | { kind: 'place'; place: Place }
  | { kind: 'help'; match: HelpMatch }
  | { kind: 'all' };

function choiceId(choice: Choice): string {
  if (choice.kind === 'hit') return `ck-${choice.hit.id}`;
  if (choice.kind === 'place') return `ck-place-${choice.place.id}`;
  if (choice.kind === 'help') return `ck-help-${choice.match.section.id}`;
  return 'ck-all';
}

function clipped(text: string): string {
  const plain = text.replaceAll('**', '');
  return plain.length > SUMMARY_MOST
    ? `${plain.slice(0, SUMMARY_MOST - 1).trimEnd()}…`
    : plain;
}

const ICON: Record<Hit['kind'], IconName> = {
  agreement: 'doc',
  workflow: 'grid',
  counterparty: 'users',
  packet: 'inbox',
  person: 'users',
};

/**
 * Search, over everything.
 *
 * The server answers, which is what makes this a search rather than a filter:
 * it reaches the whole estate rather than the page the browser happens to hold,
 * and it can only ever return rows this person could already open, because it
 * runs on their own session.
 *
 * Beneath what the server found, the help topics that mention every word, so
 * "withdraw" finds how withdrawing works as well as what could be withdrawn.
 * The guide ships with the release and is the same for everybody, so it is
 * searched here rather than asked for.
 *
 * Typing debounces the request, not the input — the field stays immediate even
 * while a search is in flight.
 */
export function CommandPalette({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [term, setTerm] = useState('');
  const [settled, setSettled] = useState('');
  const [cursor, setCursor] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);

  const { data, isFetching, isError } = useQuery(searchQuery(settled));
  const hits = data?.hits ?? [];
  const searching = settled.trim().length >= 2;
  const guide = useQuery({ ...guideQuery, enabled: searching });
  const help =
    searching && guide.data ? helpMatches(guide.data, settled, HELP_SHOWN) : [];
  const { data: permissions } = useQuery(permissionsQuery());
  const places = searching ? placesMatching(settled, permissions, PLACES_SHOWN) : [];
  const found: Choice[] = [
    ...hits.map((hit): Choice => ({ kind: 'hit', hit })),
    ...places.map((place): Choice => ({ kind: 'place', place })),
    ...help.map((match): Choice => ({ kind: 'help', match })),
  ];
  // Always last, once the server has answered: the Agreements list itself,
  // searched for the same words, for when the title is not enough. Not before,
  // so the list does not grow under the cursor while it is being read.
  const choices: Choice[] =
    searching && !isFetching ? [...found, { kind: 'all' }] : found;

  useEffect(() => {
    input.current?.focus();
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSettled(term);
      setCursor(0);
    }, SETTLE_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [term]);

  const open = (choice: Choice) => {
    onClose();
    if (choice.kind === 'help') {
      void navigate({ to: '/help', hash: choice.match.section.id });
      return;
    }
    if (choice.kind === 'all') {
      void navigate({ to: '/agreements', search: { q: settled.trim() } });
      return;
    }
    if (choice.kind === 'place') {
      const where = choice.place.open;
      if (where.to === '/agreements') {
        void navigate({ to: where.to, search: where.search ?? {} });
      } else {
        void navigate({ to: where.to });
      }
      return;
    }
    const hit = choice.hit;
    if (hit.kind === 'agreement') {
      void navigate({ to: '/agreements/$agreementId', params: { agreementId: hit.id } });
    } else if (hit.kind === 'workflow') {
      void navigate({ to: '/workflows/$workflowId', params: { workflowId: hit.id } });
    } else if (hit.kind === 'packet') {
      void navigate({ to: '/packets/$packetId', params: { packetId: hit.id } });
    } else if (hit.kind === 'counterparty') {
      void navigate({ to: '/counterparties' });
    } else {
      void navigate({ to: '/settings/people' });
    }
  };

  /** Arrow keys move, Enter opens. A palette you can only click is half of one. */
  const onKey = (event: React.KeyboardEvent) => {
    if (choices.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setCursor((at) => (at + 1) % choices.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setCursor((at) => (at - 1 + choices.length) % choices.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const chosen = choices[cursor];
      if (chosen) open(chosen);
    }
  };

  useEffect(() => {
    list.current
      ?.querySelector('[aria-selected="true"]')
      ?.scrollIntoView({ block: 'nearest' });
  }, [cursor]);

  const short = term.trim().length > 0 && term.trim().length < (data?.min_term ?? 2);

  return (
    <Modal title="Search Vellum" onClose={onClose}>
      <label className="sr" htmlFor="ck">
        Search agreements, workflows, counterparties, packets, screens and help
      </label>
      <input
        className="inp"
        id="ck"
        type="search"
        ref={input}
        value={term}
        placeholder="Search agreements, screens, people and help"
        autoComplete="off"
        role="combobox"
        aria-expanded={choices.length > 0}
        aria-controls="ck-results"
        aria-activedescendant={choices[cursor] ? choiceId(choices[cursor]) : undefined}
        onChange={(event) => {
          setTerm(event.target.value);
        }}
        onKeyDown={onKey}
      />

      <div
        className="cmdk"
        id="ck-results"
        role="listbox"
        aria-label="Results"
        ref={list}
      >
        {term.trim().length === 0 ? (
          <p className="mut">
            Search by a name, a reference or a counterparty, find a screen or a dashboard
            card, or ask how something works. Only what you are entitled to see is
            searched.
          </p>
        ) : null}

        {short ? <p className="mut">Keep typing — two letters or more.</p> : null}

        {!short && term.trim().length > 0 && isFetching && hits.length === 0 ? (
          <Rows rows={3} />
        ) : null}

        {isError ? (
          <p className="mut">
            Search is not answering. Your term is still here — try again in a moment.
          </p>
        ) : null}

        {!short &&
        !isFetching &&
        !isError &&
        settled.trim().length >= 2 &&
        found.length === 0 ? (
          <p className="mut">
            Nothing matches “{settled}”. Only what you are entitled to see was searched,
            so it may exist and not be yours.
          </p>
        ) : null}

        {choices.map((choice, index) => {
          const heading =
            choice.kind === 'place' && index === hits.length
              ? 'In Vellum'
              : choice.kind === 'help' && index === hits.length + places.length
                ? 'Help'
                : null;
          return (
            <Fragment key={choiceId(choice)}>
              {heading ? (
                <p className="cmdk-h" role="presentation">
                  {heading}
                </p>
              ) : null}
              <button
                type="button"
                id={choiceId(choice)}
                role="option"
                aria-selected={index === cursor}
                className={index === cursor ? 'on' : ''}
                onMouseEnter={() => {
                  setCursor(index);
                }}
                onClick={() => {
                  open(choice);
                }}
              >
                {choice.kind === 'hit' ? (
                  <>
                    <Icon name={ICON[choice.hit.kind]} size={15} />
                    <span>
                      <b>{choice.hit.title}</b>
                      <small>
                        {KIND_LABEL[choice.hit.kind]} · {choice.hit.detail}
                      </small>
                    </span>
                  </>
                ) : choice.kind === 'place' ? (
                  <>
                    <Icon name="panel" size={15} />
                    <span>
                      <b>{choice.place.title}</b>
                      <small>{choice.place.where}</small>
                    </span>
                  </>
                ) : choice.kind === 'help' ? (
                  <>
                    <Icon name="info" size={15} />
                    <span>
                      <b>{choice.match.section.title}</b>
                      <small>
                        Help · {choice.match.part} · {clipped(choice.match.section.lede)}
                      </small>
                    </span>
                  </>
                ) : (
                  <>
                    <Icon name="search" size={15} />
                    <span>
                      <b>Search all agreements for “{settled.trim()}”</b>
                      <small>Opens Agreements, filtered by these words</small>
                    </span>
                  </>
                )}
              </button>
            </Fragment>
          );
        })}

        {data?.truncated ? (
          <p className="mut">
            Showing the first {data.per_kind} of each kind. Narrow the term to see
            further.
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
